<template>
  <div ref="chart" />
</template>

<script>
import ApexCharts from 'apexcharts';
import dayjs from '../../core/dayjs';
import { percentFormat } from '../../helpers/percentFormat';

export default {
  props: ['fetched', 'height', 'type', 'timeseries', 'format', 'categories', 'stacked', 'legend', 'rotated', 'data', 'axis', 'ratio', 'title'],

  mounted () {
    this.$watch('source', () => this.generate(this.fetched));
    this.$watch('fetched', () => this.generate(this.fetched));
    this.$watch('type', () => this.generate(this.fetched));
    this.$watch('stacked', () => this.generate(this.fetched));
    this.$watch('rotated', () => this.generate(this.fetched));
  },

  data () {
    return {
      loading: true,
      _chart: false,
      options: {},
      defaults: {
        chart: {
          height: 400,
          toolbar: {
            show: false,
          },
          zoom: {
            enabled: true,
          },
        },
        legend: {
          show: this.legend || true,
          position: 'top',
          horizontalAlign: 'right',
          fontFamily: 'Montserrat',
          fontSize: '10px',
          labels: {
            colors: ['#71767D']
          },
          fontWeight: 500,
        },
        grid: {
          show: true,
          borderColor: '#E0DBE8',
          strokeDashArray: 2,
          position: 'back',
          xaxis: {
            lines: {
              show: false,
              // offsetX: 250,
            },
          },
          yaxis: {
            lines: {
              show: true,
            },
            labels: {
                show: true,
                style: {
                  fontSize: '10px',
                  fontWeight: 400,
                  colors: ['#ff00ff'],
                  lineHeight: '12px'
                }
            }
          },
          padding: {
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
          },
        },
        yaxis: {
          labels: {
              show: true,
              style: {
                fontSize: '10px',
                fontWeight: 400,
                colors: ['#71767D'],
                lineHeight: '12px'
              }
          }
        },
        xaxis: {
          labels: {
              show: true,
              style: {
                fontSize: '10px',
                fontWeight: 400,
                colors: ['#242424'],
                lineHeight: '12px'
              }
          }
        }
      },
    };
  },

  methods: {
    generate (fetched) {
      Vue.nextTick(() => {
        const options = this.getChartOptions(fetched);

        this.loading = false;

        if (! this._chart || this.timeseries === false) {
          if (this._chart) {
            this._chart.destroy();
            this._chart = null;
          }

          this._chart = new ApexCharts(this.$refs.chart, options);

          return this._chart.render();
        }

        return this._chart.updateOptions(options, true);
      });
    },

    getOrder (dates, sortedDates) {
      return sortedDates.map((item, index) => dates.findIndex((date) => date.getTime() === item.getTime()));
    },

    getDates (dates) {
      return dates.map((item) => new Date(dayjs(item).format('YYYY/MM/DD')));
    },

    getSortedDates (dates) {
      // @todo move to own fn file
      return [...dates].sort((left, right) => {
        const diff = left.getTime() - right.getTime();

        if (diff < 0) {
          return -1;
        }

        if (diff > 0) {
          return 1;
        }

        return diff;
      });
    },

    getChartOptions (fetched) {
      const options = this.defaults;

      options.chart.type = this.type || fetched.options.chart.type;
      options.chart.stacked = this.stacked;

      if (this.height) {
        options.chart.height = this.height;
      }

      if (this.title) {
        options.title = {
          text: this.title,
          style: {
            color: '#71767D'
          }
        };
      }

      options.plotOptions = {};

      let ticks = fetched.data.categories.length;

      if (! this.stacked) {
        ticks *= 3;
      }

      if (this.type === 'bar') {
        let columnWidth = 1 / ticks * 100;

        if (columnWidth < 1.5) {
          columnWidth = 15;
        }

        if (columnWidth > 4 && columnWidth <= 10) {
          columnWidth = 18;
        }

        if (columnWidth > 10) {
          columnWidth = 15;
        }

        options.plotOptions.bar = {
          horizontal: false,
          // distributed: true,
          // endingShape: 'rounded',
          columnWidth: `${columnWidth}%`,
        };

        if (fetched.options.chart.percentage) {
          options.tooltip = {
            y: {
              formatter: function(value) {
                return percentFormat(value, 1);
              }
            }
          };
        }
      }

      options.xaxis = {
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        tickPlacement: 'between',
      };

      options.dataLabels = {
        enabled: false,
      };

      const dates = this.getDates(fetched.data.categories);

      const sortedDates = this.getSortedDates(dates);

      if (this.timeseries) {
        options.xaxis = {
          ...options.xaxis,
          // categories: fetched.data.categories.map((category) => category + ' GMT'),
          categories: sortedDates.map((item) => dayjs(item).format(this.format)),
        };
        // options.xaxis.labels = {format: this.format};
      } else {
        options.xaxis = {
          ...options.xaxis,
          // type: 'category',
          categories: fetched.data.categories,
          // labels: {}
        };
      }

      if (this.axis === false) {
        options.xaxis.labels.show = false;
      }

      if (ticks > 25) {
        options.xaxis.labels = options.xaxis.labels || {};
        options.xaxis.labels.rotate = -45;
        options.xaxis.labels.rotateAlways = true;
        options.xaxis.labels.hideOverlappingLabels = false;
      }

      const order = this.getOrder(dates, sortedDates);

      options.labels = [];
      options.colors = fetched.data.colors;
      options.series = ! this.timeseries
        ? fetched.data.series
        : fetched.data.series.map((item) => {
          const data = order.map((value) => item.data[value]);

          return { ...item, data };
        });

      if (this.legend === false) {
        options.legend = { show: false };
      }

      if (['pie', 'donut'].indexOf(options.chart.type) !== -1) {
        options.stroke = {
          width: 0,
        };
        options.tooltip = {
          style: {
            fontSize: '12px',
            fontFamily: undefined,
          },
          custom: ({
            series, seriesIndex, dataPointIndex, w,
          }) => {
            let total = 0;

            for (const x of series) {
              total += x;
            }

            const selected = series[seriesIndex];

            return `${w.config.labels[seriesIndex]
            }: <span class="value">${selected}</span>`
                + `<span class="percent">${(selected / total * 100).toFixed(0)}%</span>`;
          },
        };
        options.series = fetched.data.labeled.series;
        options.labels = fetched.data.labeled.labels;
      } else {
        options.stroke = {};
      }

      return options;
    },
  },
};
</script>
